import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    HStack,
    Image,
    Spinner,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack
} from "@chakra-ui/react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {deleteEventNew, editEventNew, getEventsNew} from "../../../../data/api/Customer";
import {EventModelNew, EventQueryParamsNew} from "../../../../data/model/Event";
import {AddIcon} from "@chakra-ui/icons";
import {useNavigate} from "react-router-dom";
import {
    PaginationInputComponent
} from "../../../../components/molecules/PaginationInputComponent/PaginationInputComponent";
import {usePaginationData} from "../../../../utils/Utils";
import {FaArchive, FaPen, FaStar, FaTrash} from "react-icons/all";
import {ColorsClass} from "../../../../theme/colors";
import {FeaturedDialogContent} from "../../../../components/molecules/ProductCard/ProductCard";
import {useDialogStore} from "../../../../store/useAlertDialog";

function getFormattedDataFromTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

export const EventsPage: React.FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState<number>(1);

    const [archived, setArchived] = useState(false);

    const response = useQuery({
        queryKey: ["get-events-mine", archived],
        queryFn: () => getEventsNew({page: page - 1, archived}),
        refetchOnWindowFocus: "always"
    });

    const {data: events, totPage, isLoading, refetch} = usePaginationData<EventModelNew[]>(response);

    return (
        <VStack w={'100%'} h={'100%'} overflow={'hidden'}>
            <VStack w="100%" alignItems="flex-start"
                    overflow={'hidden'} position="relative">
                <HStack pt="24px" w={'100%'}>
                    <Text fontSize="24px" fontWeight="bold" ms="24px">
                        I miei eventi
                    </Text>
                    <PaginationInputComponent setPage={setPage} page={page} totPage={totPage}/>
                    <HStack mr={6} flex={1} justifyContent={'flex-end'}>
                        <Button w={164} _hover={{
                            backgroundColor: archived ? ColorsClass.chatbotBlueHover : ColorsClass.white
                        }} onClick={() => setArchived(!archived)}
                                backgroundColor={archived ? ColorsClass.chatbotBlue : undefined} gap={2}>
                            <FaArchive size={14} color={archived ? ColorsClass.white : ColorsClass.chatbotBlue}/>
                            <Text
                                color={archived ? ColorsClass.white : ColorsClass.chatbotBlue}>{archived ? "Tutti" : "Archiviati"}</Text>
                        </Button>
                    </HStack>
                </HStack>

                <Box
                    mt="18px"
                    mx="24px"
                    marginRight={6}
                    borderRadius="16px"
                    alignSelf="stretch"
                    paddingBottom={"100px"}
                    backgroundColor="white"
                    overflowX="auto"
                >
                    <Table variant="simple" minWidth="800px">
                        <Thead>
                            <Tr>
                                <Th></Th>
                                <Th>ID</Th>
                                <Th>Titolo</Th>
                                <Th>Data</Th>
                                <Th>Luogo</Th>
                                <Th position="sticky" right="0" bg="white">
                                </Th>
                            </Tr>
                        </Thead>
                        {isLoading ? (
                            <Tbody>
                                <Tr>
                                    <Td colSpan={6} textAlign="center">
                                        Caricamento...
                                    </Td>
                                </Tr>
                            </Tbody>
                        ) : (
                            <EventsTableBody data={events} refresh={refetch}/>
                        )}
                    </Table>
                </Box>
            </VStack>
            <HStack
                cursor="pointer"
                h="64px"
                px="24px"
                borderRadius="24px"
                position="absolute"
                alignItems="center"
                justifyContent="center"
                bottom="16px"
                right="16px"
                backgroundColor="secondary.200"
                _hover={{backgroundColor: "tertiary.200"}}
                onClick={() => {
                    navigate("/events/add");
                }}
            >
                <AddIcon w="16px" h="16px"/>
                <Text ms="8px" fontWeight="bold">
                    Aggiungi
                </Text>
            </HStack>
        </VStack>
    );
};

interface EventsTableBodyProps {
    data?: EventModelNew[];
    refresh: () => void;
}

const EventsTableBody: React.FC<EventsTableBodyProps> = ({data, refresh}) => {
    return (
        <Tbody overflowY={'auto'}>
            {data && data.length > 0 ? (
                data.map((event, index) => (
                    <EventsTableComponent key={`event_${index}`} model={event} refresh={refresh}/>
                ))
            ) : (
                <Tr>
                    <Td colSpan={6} textAlign="center">
                        Nessun evento trovato.
                    </Td>
                </Tr>
            )}
        </Tbody>
    );
};

interface EventsTableComponentProps {
    model: EventModelNew;
    refresh: () => void;
}

export const EventsTableComponent: React.FC<EventsTableComponentProps> = ({model, refresh}) => {
    const {id, title, startDate, endDate, location, mediaList, archived, inEvidence} = model;

    const mainImage = mediaList?.find(item => item.isMain)?.url;

    const navigate = useNavigate();
    const {mutateAsync} = useMutation({
        mutationKey: ['delete-event'],
        mutationFn: (id: string) => deleteEventNew(id),
    });

    const {mutateAsync: archiveAsync, isPending: isPendingArchive} = useMutation({
        mutationKey: ['archive-event', id],
        mutationFn: ({id, data}: { id: string, data: EventQueryParamsNew }) => editEventNew(id, data),
    });

    const {showDialog, closeDialog} = useDialogStore();

    const isAdmin = localStorage.getItem("userType") === 'admin';

    const fullDate = useMemo(() => {
        if (startDate && endDate)
            return `${getFormattedDataFromTimestamp(startDate)} - ${getFormattedDataFromTimestamp(endDate)}`;
        else if (startDate) return getFormattedDataFromTimestamp(startDate);
        else if (endDate) return getFormattedDataFromTimestamp(endDate);
        else return "-";
    }, [startDate, endDate]);

    return (
        <Tr bg={"white"} cursor={'pointer'} onClick={() => {
            navigate(`/events/${id}`);
        }}>
            <Td>
                <Stack
                    w="64px"
                    h="64px"
                    minW="64px"
                    minH="64px"
                    backgroundColor="white"
                    borderWidth="1px"
                    borderColor="white"
                    borderRadius="16px"
                    overflow="hidden"
                >
                    <Image
                        w="64px"
                        h="64px"
                        minW="64px"
                        minH="64px"
                        objectFit="cover"
                        src={mainImage}
                        alt={title}
                    />
                </Stack>
            </Td>
            <Td>{id}</Td>
            <Td>{title}</Td>
            <Td>{fullDate}</Td>
            <Td>{location}</Td>
            <Td position="sticky" right="0" bg="white">
                <VStack spacing={2}>
                    {isAdmin && <Button w={'100%'} backgroundColor={inEvidence ? ColorsClass.orange : undefined} gap={2}
                            onClick={(e) => {
                                e.stopPropagation();
                                showDialog({
                                    title: "In evidenza",
                                    description: "Inserisci le informazioni da mostrare nel prodotto in evidenza",
                                    cancelButtonText: "Annulla",
                                    onPressCancelButton: () => closeDialog(),
                                    children: (
                                        <VStack w={'100%'} h={'100%'} alignItems={'stretch'}>
                                            <FeaturedDialogContent featuredItem={(model || {}).inEvidence}
                                                                   item={model || {}}
                                                                   type={"event"}
                                                                   close={async ({refetch: refetchBool}) => {
                                                                       if (refetchBool) await refresh();
                                                                       closeDialog();
                                                                   }}/>
                                        </VStack>
                                    )
                                });
                            }} size="sm">
                        <FaStar color={inEvidence ? 'white' : 'black'} size={14}/>
                        <Text color={inEvidence ? 'white' : 'black'} fontSize={14}>In evidenza</Text>
                    </Button>}
                    <Button disabled={isPendingArchive} backgroundColor={archived ? ColorsClass.chatbotBlue : undefined}
                            w={'100%'} gap={2}
                            onClick={async (e) => {
                                e.stopPropagation();
                                await archiveAsync({
                                    id: id || "",
                                    data: {
                                        archived: !archived
                                    }
                                }).finally(() => {
                                    refresh();
                                    closeDialog();
                                })
                            }} size="sm">
                        {isPendingArchive ? <Spinner/> : <>
                            <FaArchive color={archived ? 'whitesmoke' : 'black'} size={14}/>
                            <Text color={archived ? 'whitesmoke' : 'black'} fontSize={14}>Archivia</Text>
                        </>}
                    </Button>
                    <Button w={'100%'} gap={2} onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/events/edit/${id}`);
                    }} size="sm">
                        <FaPen color={'black'} size={14}/>
                        <Text fontSize={14}>Modifica</Text>
                    </Button>
                    <Button w={'100%'} gap={2} onClick={(e) => {
                        e.stopPropagation();
                        id && mutateAsync(id).then(refresh);
                    }} size="sm">
                        <FaTrash color={ColorsClass.primaryRed} size={14}/>
                        <Text color={ColorsClass.primaryRed} fontSize={14}>Elimina</Text>
                    </Button>
                </VStack>
            </Td>
        </Tr>
    );
};
